import { DomainSearchResult } from "@/services/dreamscapeApi";
import { GradientButton } from "@/components/common/ui/Button";
import { CheckCircle2, XCircle, Loader2, ChevronLeft, ChevronRight, ShoppingCart } from "lucide-react";

interface DomainSearchResultsProps {
  results: DomainSearchResult[];
  loading?: boolean;
  onRegister?: (domain: string) => void;
  currentPage?: number;
  resultsPerPage?: number;
  onPageChange?: (page: number) => void;
  onLoadMore?: () => void;
}

export default function DomainSearchResults({
  results,
  loading = false,
  onRegister,
  currentPage = 1,
  resultsPerPage = 7,
  onPageChange,
  onLoadMore,
}: DomainSearchResultsProps) {
  if (loading) {
    return (
      <div className="mt-8 flex items-center justify-center gap-3 text-white">
        <Loader2 className="w-6 h-6 animate-spin" />
        <span>Searching domains...</span>
      </div>
    );
  }

  // Filter out domains with checking errors (invalid TLDs)
  const validResults = results.filter(result => {
    // Remove results with "Invalid TLD" errors
    if (result.checkingError && result.checkingError.toLowerCase().includes('invalid tld')) {
      return false;
    }
    return true;
  });

  if (validResults.length === 0) {
    return null;
  }

  // Calculate pagination
  const totalPages = Math.ceil(validResults.length / resultsPerPage);
  const startIndex = (currentPage - 1) * resultsPerPage;
  const endIndex = startIndex + resultsPerPage;
  const paginatedResults = validResults.slice(startIndex, endIndex);

  return (
    <div className="mt-8 space-y-4">
      <h3 className="text-2xl font-bold text-white mb-4">Domain Search Results</h3>
      <div className="space-y-3">
        {paginatedResults.map((result, index) => (
          <div
            key={index}
            className={`flex items-center justify-between p-4 rounded-xl border border-white/[0.08] transition-all ${result.available
              ? "bg-white/[0.03] hover:border-white/20 shadow-sm shadow-black/5"
              : "bg-white/[0.01] opacity-60"
              }`}
          >
            <div className="flex items-center gap-4 flex-1 min-w-0">
              <div className="flex-shrink-0">
                {result.available ? (
                  <CheckCircle2 className="w-6 h-6 text-green-400" />
                ) : (
                  <XCircle className="w-6 h-6 text-red-400" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-3 flex-wrap">
                  <span className="text-xl font-semibold text-white truncate">
                    {result.domain}
                  </span>
                  <span
                    className={`text-sm font-medium px-2 py-1 rounded ${result.available
                      ? "text-green-400 bg-green-400/10"
                      : "text-red-400 bg-red-400/10"
                      }`}
                  >
                    {result.available ? "Available" : "Unavailable"}
                  </span>
                </div>
                {!result.available && (
                  <div className="text-xs text-textColor/70 mt-1">
                    Already registered, you can get it on Backorder
                  </div>
                )}
              </div>
            </div>
            <div className="flex items-center gap-6">
              {/* Action Button */}
              {result.available ? (
                <GradientButton
                  onClick={() => onRegister?.(result.domain)}
                  variant="dashboard"
                  className="px-6 py-2 whitespace-nowrap min-w-[140px] text-sm flex items-center gap-2"
                >
                  <ShoppingCart className="w-4 h-4" />
                  Secure your Domain
                </GradientButton>
              ) : (
                <GradientButton
                  onClick={() => onRegister?.(result.domain)}
                  variant="primary"
                  className="px-6 py-2 whitespace-nowrap min-w-[140px] text-sm"
                >
                  Backorder
                </GradientButton>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Load More Suggestions Button */}
      {onLoadMore && (
        <div className="flex justify-center mt-10">
          <div className="relative group">
            <div className="absolute -inset-4 blur-2xl opacity-0 group-hover:opacity-30 bg-gradient-to-r from-cyan-500 to-blue-600 transition-opacity duration-500 rounded-full"></div>
            <div className="relative">
              <GradientButton
                onClick={onLoadMore}
                className="px-10 py-4 flex items-center gap-3 h-auto"
              >
                <span className="font-bold tracking-wider text-base uppercase">
                  Load more suggestions
                </span>
                <ChevronRight className="w-5 h-5 group-hover:translate-x-0.5 transition-transform" />
              </GradientButton>
            </div>
          </div>
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-10 pt-6 border-t border-white/5">
          <div className="text-sm text-textColor">
            Showing {startIndex + 1} to {Math.min(endIndex, validResults.length)} of {validResults.length} domains
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => onPageChange?.(currentPage - 1)}
              disabled={currentPage === 1}
              className={`px-4 py-2 rounded-lg border transition-colors ${currentPage === 1
                ? 'bg-cardBg/50 border-primary/20 text-textColor/50 cursor-not-allowed'
                : 'bg-cardBg border-primary/30 text-white hover:bg-primary/10'
                }`}
            >
              <ChevronLeft className="w-4 h-4" />
            </button>

            <div className="flex items-center gap-1">
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => {
                // Show first page, last page, current page, and pages around current
                if (
                  page === 1 ||
                  page === totalPages ||
                  (page >= currentPage - 1 && page <= currentPage + 1)
                ) {
                  return (
                    <button
                      key={page}
                      onClick={() => onPageChange?.(page)}
                      className={`px-3 py-2 rounded-lg border transition-colors ${page === currentPage
                        ? 'bg-primary text-white border-primary'
                        : 'bg-cardBg border-primary/30 text-white hover:bg-primary/10'
                        }`}
                    >
                      {page}
                    </button>
                  );
                } else if (page === currentPage - 2 || page === currentPage + 2) {
                  return <span key={page} className="px-2 text-textColor">...</span>;
                }
                return null;
              })}
            </div>

            <button
              onClick={() => onPageChange?.(currentPage + 1)}
              disabled={currentPage === totalPages}
              className={`px-4 py-2 rounded-lg border transition-colors ${currentPage === totalPages
                ? 'bg-cardBg/50 border-primary/20 text-textColor/50 cursor-not-allowed'
                : 'bg-cardBg border-primary/30 text-white hover:bg-primary/10'
                }`}
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

